import Loading from 'app/components/Loading/LoadingScreen';
import { initFeatureFlag } from 'app/config/configFeatureFlag';
import { setSentryTenant } from 'app/config/configSentry';
import { initializeLogin } from 'app/config/initializeApp';
import { initializeLanguage } from 'app/config/initializeLanguage';
import { initializeLemonade } from 'app/config/initializeLemonade';
import { initializePersonalice } from 'app/config/initializePersonalice';
import RoutesContext from 'app/context/routes.context';
import useFeatureFlagRoutes from 'app/hooks/useFeatureFlagRoutes';
import useLastLocation from 'app/hooks/useLastLocation';
import LogoutPage from 'app/pages/auth/LogoutPage';
import HomePage from 'app/pages/home/HomePage';
import ScormNormalize from 'app/pages/scormNormalize';
import UIPage from 'app/pages/ui/UIPage';
import ViewerContainer from 'app/pages/viewer/ViewerContainer';
import ViewersContainer from 'app/pages/viewers/ViewersContainer';
import { canAccess } from 'app/policies/routes/routes.access';
import * as routerHelpers from 'app/services/router.service';
import { getRedirect } from 'app/services/routes.service';
import { app, scopes, systemSettings } from 'app/store/index';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation, withRouter } from 'react-router-dom';
import routes from './routesConfig';
import routesAuth from './routesConfigAuth';

export const Routes = withRouter(() => {
  const flags = useFeatureFlagRoutes();
  const SCOPES = useSelector((state) => scopes.selectors.getScopes(state));
  const isLoading = useSelector((state) => app.selectors.getLoading(state));
  const lastLocation = useLastLocation();
  const location = useLocation();
  const organization = useSelector((state) => systemSettings.selectors.getOrganization(state));
  const settings = useSelector((state) => systemSettings.selectors.getSetting(state));
  const [transitionPage, setTransistionPage] = React.useState('fadeIn'); // fadeIn, fadeOut
  const prevPathnameRef = React.useRef(location.pathname);
  const { isAuthorized, user } = useSelector(
    ({ auth }) => ({
      hasAuthToken: Boolean(auth.authToken),
      isAuthorized: auth.user != null,
      roleUser: (auth.user?.role_guid) || null,
      user: auth.user,
      userLastLocation: routerHelpers.getLastLocation(),
    }),
    shallowEqual
  );
  const redirectUrl = getRedirect(user);

  React.useEffect(() => {
    if (lastLocation && isAuthorized) {
      routerHelpers.saveLastLocation(lastLocation);
    }
  }, [lastLocation, isAuthorized]);

  // Manejar animación de transición cuando cambia la ruta
  React.useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      // Nueva ruta: iniciar fadeOut, luego fadeIn
      setTransistionPage('fadeOut');
      const timeout = setTimeout(() => {
        setTransistionPage('fadeIn');
      }, 150);

      prevPathnameRef.current = location.pathname;

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    initializeLanguage(user);
    if (isAuthorized) {
      initializeLogin();
    }
  }, [isAuthorized]);

  React.useEffect(() => {
    if (isAuthorized) {
      initializeLemonade({ showLemonadeAiFeedback: flags?.showLemonadeAiFeedback, user });
    }
  }, [isAuthorized, flags?.showLemonadeAiFeedback]);

  React.useEffect(() => {
    initializePersonalice({ organization, scopes: SCOPES });
  }, [organization]);

  React.useEffect(() => {
    if (organization) {
      setSentryTenant(organization);
    }
  }, [organization]);

  React.useEffect(() => {
    if (organization && user) {
      initFeatureFlag({ organization, user });
    }
  }, [organization, user]);

  return (
    <RoutesContext.Provider value={{ transitionPage }}>
      <div>
        <Switch location={location}>
          <Route path="/logout" component={LogoutPage} />

          {!isAuthorized ? (
            <Switch>
              {routesAuth.map((item) => (
                <Route key={item.path} path={item.path} component={item.component} />
              ))}

              <Redirect to="/auth/login" from="/auth" exact={true} />
              <Redirect to="/auth/login" />
            </Switch>
          ) : (
            <Redirect from="/auth" to={redirectUrl} />
          )}

          {!isAuthorized ? (
            <Redirect to="/auth/login" />
          ) : (
            <Switch>
              <Route path="/home" render={() => <HomePage />} />
              <Route path="/ui" render={() => <UIPage />} />
              <Route path="/viewer/:id" render={() => <ViewerContainer />} />
              <Route path="/viewer-nutrient-standalone/:id" render={() => <ViewerContainer />} />
              <Route path="/viewer-v2/:id" render={() => <ViewerContainer />} />
              <Route path="/viewer-v1/:id" render={() => <ViewersContainer />} />
              <Route path="/scormnormalize" render={() => <ScormNormalize />} />

              {routes.map((item) =>
                canAccess({ flags, path: item.path, settings, user }) ? <Route key={item.path} path={item.path} component={item.component} /> : null
              )}

              <Redirect from="*" to={redirectUrl} />
            </Switch>
          )}
        </Switch>
      </div>

      {isLoading && <Loading />}
    </RoutesContext.Provider>
  );
});
